import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { geoFiltersToDatatableUrl } from '../../utils/geoToDatatable';
import { apiPath } from '../../apiurl.js';
import LoadingSequence from '../../assets/Loading/LoadingSequence';
import { countryNameToIso } from '../../assets/Map/countryNameToIso';
import CountryMap, { DetailPanel, DetailStats, TopList, escapeHtml } from './CountryMap';
import LargeRunModal, { LARGE_RUN_THRESHOLD, readHitsPath } from '../LargeRunModal/LargeRunModal';

const COUNTS_URL = `${apiPath}/api/dashboard-status/metadata-counts`;
const GEO_URL    = `${apiPath}/api/dashboard-status/metadata-geo`;
const DETAIL_URL = `${apiPath}/api/dashboard-status/metadata-country`;
const TOP_RUNS_URL = `${apiPath}/api/dashboard-status/metadata-top-runs`;
const RUN_URL      = `${apiPath}/api/dashboard-status/metadata-run`;
const RUN_ID_RE    = /^[A-Z]{3}\d{3,12}$/;

// Option lists hide the same placeholder values the backend leaves off the map.
const JUNK = new Set(['uncalculated', 'missing', 'not collected', 'not applicable',
  'not provided', 'n/a', 'na', 'unknown', 'none', 'null']);
const isJunk = (label) => JUNK.has(String(label || '').trim().toLowerCase());

// Sequential blue scale, light → dark, by sample count.
const COUNT_BUCKETS = [
  { min: 10000, color: '#1e3a8a', label: '10k+'     },
  { min: 1000,  color: '#1d4ed8', label: '1k–10k'   },
  { min: 100,   color: '#3b82f6', label: '100–1k'   },
  { min: 10,    color: '#93c5fd', label: '10–100'   },
  { min: 1,     color: '#dbeafe', label: '1–10'     },
];
const colorForCount = (n) => (COUNT_BUCKETS.find(b => n >= b.min) || COUNT_BUCKETS[COUNT_BUCKETS.length - 1]).color;

// ─────────────────────────────────────────────────────────────────────────────
//  Searchable multi-select
// ─────────────────────────────────────────────────────────────────────────────

function MultiSelect({ label, options, selected, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (q ? options.filter(o => o.label.toLowerCase().includes(q)) : options).slice(0, 100);
  }, [options, query]);

  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);

  return (
    <div className="geo-section">
      <div className="geo-section-label">
        {label} {selected.length > 0 && <span className="geo-ms-count">({selected.length} selected)</span>}
      </div>
      <div className="geo-ms" ref={boxRef}>
        <input
          className="geo-input"
          type="text"
          placeholder={placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        />
        {open && (
          <div className="geo-ms-list">
            {visible.length === 0 ? (
              <div className="geo-ms-empty">No matches</div>
            ) : visible.map(o => (
              <label key={o.label} className="geo-ms-item" title={o.label}>
                <input type="checkbox" checked={selected.includes(o.label)} onChange={() => toggle(o.label)} />
                <span className="geo-ms-label">{o.label}</span>
                <span className="geo-ms-num">{o.count.toLocaleString()}</span>
              </label>
            ))}
            {options.length > visible.length && !query && (
              <div className="geo-ms-empty">Type to search {options.length.toLocaleString()} options…</div>
            )}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="geo-chips">
          {selected.map(v => (
            <span key={v} className="geo-chip" title={v}>
              <span className="geo-chip-text">{v}</span>
              <button className="geo-chip-x" onClick={() => toggle(v)}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Filter model
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  names: [], organisms: [], isoSources: [],
  countries: [], continents: [],
  centers: [], platforms: [],
  collFrom: '', collTo: '', sraFrom: '', sraTo: '',
  runStartFrom: '', runStartTo: '', runEndFrom: '', runEndTo: '',
  readHits: false, regionHits: false, hideIncomplete: false,
  minReadHits: '', minRegionHits: '', regions: [],
};

// What the tab opens with, and what "Reset to defaults" returns to — matches the Data Table.
const DEFAULT_FILTERS = { ...EMPTY_FILTERS, readHits: true, regionHits: true };
const DEFAULT_KEY = JSON.stringify(DEFAULT_FILTERS);
const isDefault = (f) => JSON.stringify(f) === DEFAULT_KEY;

// Data Table–style quick toggles above the map (apply immediately).
const QUICK_TOGGLES = [
  { key: 'readHits',       label: 'ReadHits Filter', title: 'Only runs where bowtie2 found poliovirus reads (ReadHits = true)' },
  { key: 'hideIncomplete', label: 'Hide Incomplete', title: 'Hide runs with no end date and no elapsed time (still running)' },
  { key: 'regionHits',     label: 'Region Hits',     title: 'Only runs where blastN(bowtie2) RegionHits = true' },
];

const REGIONS = ['5UTR', 'VP4', 'VP2', 'VP3', 'VP1', '2A', '2B', '2C', '3A', '3B', '3C', '3D', '3UTR'];

const GROUPS = {
  sample:     ['names', 'organisms', 'isoSources'],
  location:   ['countries', 'continents'],
  sequencing: ['centers', 'platforms'],
  dates:      ['collFrom', 'collTo', 'sraFrom', 'sraTo', 'runStartFrom', 'runStartTo', 'runEndFrom', 'runEndTo'],
  tools:      ['readHits', 'regionHits', 'hideIncomplete', 'minReadHits', 'minRegionHits', 'regions'],
};

// Applied filters survive leaving the page (e.g. to the Data Table and Back).
const STORAGE_KEY = 'pass.geography.metadataFilters';
function loadSavedFilters() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (!saved || typeof saved !== 'object') return DEFAULT_FILTERS;
    const out = { ...DEFAULT_FILTERS };
    Object.keys(EMPTY_FILTERS).forEach(k => {
      if (typeof saved[k] === typeof EMPTY_FILTERS[k] && Array.isArray(saved[k]) === Array.isArray(EMPTY_FILTERS[k])) out[k] = saved[k];
    });
    return out;
  } catch { return DEFAULT_FILTERS; }
}

const isSet = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));
const countActive = (f, keys) => keys.reduce((n, k) => n + (isSet(f[k]) ? 1 : 0), 0);
const anyActive = (f) => Object.keys(f).some(k => isSet(f[k]));

// Filters → query params understood by /metadata-geo and /metadata-country.
function toParams(f) {
  const p = {};
  Object.entries(f).forEach(([k, v]) => {
    if (Array.isArray(v)) { if (v.length) p[k] = JSON.stringify(v); }
    else if (v === true) p[k] = 'true';
    else if (v) p[k] = String(v);
  });
  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Small controls
// ─────────────────────────────────────────────────────────────────────────────

function FilterGroup({ title, active, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`geo-group ${open ? 'geo-group--open' : ''}`}>
      <button type="button" className="geo-group-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="geo-group-chev" aria-hidden="true" />
        <span className="geo-group-title">{title}</span>
        {active > 0 && <span className="geo-group-badge">{active}</span>}
      </button>
      {open && <div className="geo-group-body">{children}</div>}
    </div>
  );
}

function DateRange({ label, from, to, onChange }) {
  return (
    <div className="geo-section">
      <div className="geo-section-label">{label}</div>
      <div className="geo-date-row">
        <input type="date" className="geo-input" value={from} max={to || undefined}
          onChange={e => onChange(e.target.value, to)} aria-label={`${label} from`} />
        <span className="geo-date-sep">to</span>
        <input type="date" className="geo-input" value={to} min={from || undefined}
          onChange={e => onChange(from, e.target.value)} aria-label={`${label} to`} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Metadata tab
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  Top runs list (country panel) — each row opens the run in Read Hits
// ─────────────────────────────────────────────────────────────────────────────

function TopRuns({ runs, loading, error, onOpen, initial = 5 }) {
  const [expanded, setExpanded] = useState(false);
  if (loading && !runs) return <div className="geo-ms-empty">Loading top runs…</div>;
  if (error) return <div className="geo-ms-empty">Could not load top runs.</div>;
  if (!runs?.length) return null;
  const shown = expanded ? runs : runs.slice(0, initial);
  return (
    <div className="geo-detail-section">
      <div className="geo-section-label">Top Runs <span className="geo-ms-count">by ReadHits</span></div>
      {shown.map((r, i) => (
        <button key={r.run} type="button" className="geo-run-row"
          onClick={() => onOpen(r.run, r.regionHits)}
          title={`Open ${r.run} in Read Hits — ReadHits ${r.readHits.toLocaleString()} · RegionHits ${r.regionHits.toLocaleString()}`}>
          <span className="geo-toplist-rank">{i + 1}</span>
          <span className="geo-run-main">
            <span className="geo-run-id">{r.run}</span>
            <span className="geo-run-sub">{r.organism || '—'}</span>
          </span>
          {r.regionHits > LARGE_RUN_THRESHOLD && <span className="geo-run-large" title="Large run — you'll choose filters before loading">large</span>}
          <span className="geo-toplist-num">{r.readHits.toLocaleString()}</span>
          <span className="geo-run-arrow" aria-hidden="true">→</span>
        </button>
      ))}
      {runs.length > initial && (
        <button type="button" className="geo-link-btn geo-detail-more" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `Show ${runs.length - shown.length} more`}
        </button>
      )}
    </div>
  );
}

export default function MetadataGeography({ onStats, onCompareRuns }) {
  const [options, setOptions] = useState({
    names: [], organisms: [], isoSources: [], countries: [], continents: [], centers: [], platforms: [],
  });
  const [optError, setOptError] = useState('');

  // `draft` is what the panel edits; `applied` drives the map (Apply copies draft → applied).
  const [draft,   setDraft]   = useState(loadSavedFilters);
  const [applied, setApplied] = useState(() => draft);
  const appliedKey = JSON.stringify(applied);
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, appliedKey); } catch {}
  }, [appliedKey]);

  const history = useHistory();
  const openDatatable = (countryLabels) => history.push(geoFiltersToDatatableUrl(applied, countryLabels));

  // Open one run in Read Hits. Large runs go through the filter dialog first;
  // the map's Region filter carries over as the Read Hits region filter.
  const [largeRun, setLargeRun] = useState(null);   // { run, count }
  const openRun = (run, regionHitsCount = 0) => {
    if (regionHitsCount > LARGE_RUN_THRESHOLD) setLargeRun({ run, count: regionHitsCount });
    else history.push(readHitsPath(run, { regions: applied.regions }));
  };

  // Run ID search
  const [runQuery, setRunQuery]   = useState('');
  const [runLookup, setRunLookup] = useState(null);  // { status: 'loading'|'found'|'error', data?, message? }
  const dirty = JSON.stringify(draft) !== appliedKey;
  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const [geo, setGeo]           = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError]       = useState('');
  const [selectedIso, setSelectedIso] = useState(null);
  const [detail, setDetail]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mapFocus, setMapFocus]       = useState(null);   // { isos, n } — see CountryMap
  const fitOnNextData = useRef(!isDefault(draft) && anyActive(draft));   // restored filters → fit on first load

  // Filter options (same cached endpoint the dashboard uses).
  useEffect(() => {
    axios.get(COUNTS_URL)
      .then(res => {
        const d = res.data || {};
        setOptions({
          names:      d.names            || [],
          organisms:  d.organisms        || [],
          isoSources: (d.isolationSources || []).filter(o => !isJunk(o.label)),
          countries:  (d.countries       || []).filter(o => !isJunk(o.label)),
          continents: (d.continents      || []).filter(o => !isJunk(o.label)),
          centers:    d.centerNames      || [],
          platforms:  d.platforms        || [],
        });
      })
      .catch(() => setOptError('Could not load filter options.'));
  }, []);

  // Map data — refetch when applied filters change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    axios.get(GEO_URL, { params: toParams(applied) })
      .then(res => { if (!cancelled) setGeo(res.data); })
      .catch(() => { if (!cancelled) { setGeo(null); setError('Could not load metadata map data.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appliedKey]); // eslint-disable-line

  // Roll metadata country names up to map ISO codes.
  const { byIso, unmapped } = useMemo(() => {
    const out = {}; let miss = 0;
    (geo?.countries || []).forEach(({ label, count }) => {
      const iso = countryNameToIso(label);
      if (!iso) { miss += count; return; }
      if (!out[iso]) out[iso] = { name: label, labels: [], count: 0 };
      out[iso].labels.push(label);
      out[iso].count += count;
    });
    Object.values(out).forEach(c => { c.color = colorForCount(c.count); });
    return { byIso: out, unmapped: miss };
  }, [geo]);

  // After applied results arrive: fit the map to them, or go back to the world.
  useEffect(() => {
    if (!geo || !fitOnNextData.current) return;
    fitOnNextData.current = false;
    const active = !isDefault(applied) && anyActive(applied);
    if (!active) setSelectedIso(null);
    setMapFocus(f => ({ isos: active ? Object.keys(byIso) : [], n: (f?.n || 0) + 1 }));
  }, [geo]); // eslint-disable-line

  const applyFilters = () => {
    if (!dirty) return;
    // Country filter drives the panel: newest pick opens; if the open one was removed, fall back.
    const added = draft.countries.filter(c => !applied.countries.includes(c));
    if (added.length) {
      setSelectedIso(countryNameToIso(added[added.length - 1]));
    } else if (draft.countries.length !== applied.countries.length) {
      const isos = draft.countries.map(countryNameToIso).filter(Boolean);
      if (!isos.includes(selectedIso)) setSelectedIso(isos.length ? isos[isos.length - 1] : null);
    }
    if (isDefault(draft) || !anyActive(draft)) setSelectedIso(null);
    fitOnNextData.current = true;
    setApplied(draft);
  };

  const resetToDefaults = () => {
    setDraft(DEFAULT_FILTERS);
    setSelectedIso(null);
    if (!isDefault(applied)) {
      fitOnNextData.current = true;
      setApplied(DEFAULT_FILTERS);
    } else {
      setMapFocus(f => ({ isos: [], n: (f?.n || 0) + 1 }));
    }
  };

  // "Filter map to this country": add it to the Country filter and apply right away.
  // Other unapplied edits stay in the draft.
  const filterToCountry = (labels) => {
    const countries = [...new Set([...applied.countries, ...labels])];
    fitOnNextData.current = true;
    setApplied(a => ({ ...a, countries }));
    setDraft(d => ({ ...d, countries: [...new Set([...d.countries, ...labels])] }));
  };

  // Quick toggles skip the Apply step; they also tick the matching checkbox in the panel.
  const toggleQuick = (key) => {
    const v = !applied[key];
    fitOnNextData.current = true;
    setApplied(a => ({ ...a, [key]: v }));
    setDraft(d => ({ ...d, [key]: v }));
  };
  const discardDraft = () => setDraft(applied);

  // Clicked country detail — refetch when selection or applied filters change.
  const selected = selectedIso ? byIso[selectedIso] : null;
  const selectedKey = selected ? selected.labels.join('|') : '';
  useEffect(() => {
    if (!selectedKey) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    const params = { ...toParams(applied), country: JSON.stringify(selectedKey.split('|')) };
    axios.get(DETAIL_URL, { params })
      .then(res => {
        if (cancelled) return;
        const d = res.data;
        // Guard against non-JSON 200s (e.g. the server's index.html fallback).
        if (!d || typeof d !== 'object' || typeof d.total !== 'number') { setDetail({ error: true }); return; }
        setDetail({
          total:            d.total,
          readHitsRuns:     Number(d.readHitsRuns) || 0,
          organisms:        Array.isArray(d.organisms)        ? d.organisms        : [],
          centers:          Array.isArray(d.centers)          ? d.centers          : [],
          platforms:        Array.isArray(d.platforms)        ? d.platforms        : [],
          isolationSources: Array.isArray(d.isolationSources) ? d.isolationSources : [],
        });
      })
      .catch(() => { if (!cancelled) setDetail({ error: true }); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedKey, appliedKey]); // eslint-disable-line

  // Clicked country top runs — same trigger as the detail breakdown.
  const [topRuns, setTopRuns] = useState({ runs: null, loading: false, error: false });
  useEffect(() => {
    if (!selectedKey) { setTopRuns({ runs: null, loading: false, error: false }); return; }
    let cancelled = false;
    setTopRuns(t => ({ ...t, loading: true, error: false }));
    const params = { ...toParams(applied), country: JSON.stringify(selectedKey.split('|')) };
    axios.get(TOP_RUNS_URL, { params })
      .then(res => { if (!cancelled) setTopRuns({ runs: res.data?.runs || [], loading: false, error: false }); })
      .catch(() => { if (!cancelled) setTopRuns({ runs: null, loading: false, error: true }); });
    return () => { cancelled = true; };
  }, [selectedKey, appliedKey]); // eslint-disable-line

  const findRun = async (e) => {
    e?.preventDefault();
    const q = runQuery.trim().toUpperCase();
    if (!q) { setRunLookup(null); return; }
    if (!RUN_ID_RE.test(q)) {
      setRunLookup({ status: 'error', message: 'Enter a run accession like SRR31806855.' });
      return;
    }
    setRunLookup({ status: 'loading' });
    try {
      const { data } = await axios.get(`${RUN_URL}/${encodeURIComponent(q)}`);
      const iso = data.country && !isJunk(data.country) ? countryNameToIso(data.country) : null;
      setRunLookup({ status: 'found', data: { ...data, iso } });
      if (iso) setMapFocus(f => ({ isos: [iso], n: (f?.n || 0) + 1 }));
    } catch (err) {
      const status = err?.response?.status;
      setRunLookup({
        status: 'error',
        message: status === 404 ? `Run ${q} was not found.` : (err?.response?.data?.error || 'Could not look up that run.'),
      });
    }
  };
  const closeRunCard = () => { setRunLookup(null); };

  const tooltipHtml = (iso, c) => `
    <div class="geo-tooltip-title"><span class="geo-tooltip-dot" style="background:${c.color}"></span>${escapeHtml(c.labels.join(' / '))}</div>
    <div class="geo-tooltip-row"><span>Samples</span><span class="geo-tooltip-val">${c.count.toLocaleString()}</span></div>
    ${geo?.total ? `<div class="geo-tooltip-row"><span>Share</span><span class="geo-tooltip-val">${((c.count / geo.total) * 100).toFixed(1)}%</span></div>` : ''}
    <div class="geo-tooltip-hint">Click to zoom in</div>`;

  const sampleTotal  = geo?.total || 0;
  const countryTotal = Object.keys(byIso).length;
  const noCountry    = (geo?.unassigned || 0) + unmapped;

  useEffect(() => { onStats?.({ samples: sampleTotal, countries: countryTotal }); }, [sampleTotal, countryTotal]); // eslint-disable-line

  const topCountries = useMemo(
    () => Object.values(byIso).sort((a, b) => b.count - a.count).slice(0, 10),
    [byIso]
  );

  const toggleRegion = (r) =>
    setField('regions', draft.regions.includes(r) ? draft.regions.filter(x => x !== r) : [...draft.regions, r]);

  const appliedCount = Object.values(GROUPS).reduce((n, keys) => n + countActive(applied, keys), 0);

  return (
    <>
      {/* ── LEFT FILTER PANEL ── */}
      <div className="geo-panel">
        <div className="geo-panel-header">
          <span className="geo-panel-header-title">Metadata Filters</span>
          {appliedCount > 0 && <span className="geo-map-badge">{appliedCount} applied</span>}
        </div>
        <div className="geo-panel-body">
          {optError && <div className="geo-alert geo-alert-warn">{optError}</div>}

          <FilterGroup title="Sample" active={countActive(draft, GROUPS.sample)} defaultOpen>
            <MultiSelect label="Name" placeholder="Search names…"
              options={options.names} selected={draft.names} onChange={v => setField('names', v)} />
            <MultiSelect label="Organism" placeholder="Search organisms…"
              options={options.organisms} selected={draft.organisms} onChange={v => setField('organisms', v)} />
            <MultiSelect label="Isolation Source" placeholder="Search isolation sources…"
              options={options.isoSources} selected={draft.isoSources} onChange={v => setField('isoSources', v)} />
          </FilterGroup>

          <FilterGroup title="Location" active={countActive(draft, GROUPS.location)} defaultOpen>
            <MultiSelect label="Country" placeholder="Search countries…"
              options={options.countries} selected={draft.countries} onChange={v => setField('countries', v)} />
            <MultiSelect label="Continent" placeholder="Search continents…"
              options={options.continents} selected={draft.continents} onChange={v => setField('continents', v)} />
          </FilterGroup>

          <FilterGroup title="Sequencing" active={countActive(draft, GROUPS.sequencing)}>
            <MultiSelect label="Center" placeholder="Search centers…"
              options={options.centers} selected={draft.centers} onChange={v => setField('centers', v)} />
            <MultiSelect label="Platform" placeholder="Search platforms…"
              options={options.platforms} selected={draft.platforms} onChange={v => setField('platforms', v)} />
          </FilterGroup>

          <FilterGroup title="Dates" active={countActive(draft, GROUPS.dates)}>
            <DateRange label="Collection Date" from={draft.collFrom} to={draft.collTo}
              onChange={(a, b) => setDraft(d => ({ ...d, collFrom: a, collTo: b }))} />
            <DateRange label="SRA File Date" from={draft.sraFrom} to={draft.sraTo}
              onChange={(a, b) => setDraft(d => ({ ...d, sraFrom: a, sraTo: b }))} />
            <DateRange label="Run Start" from={draft.runStartFrom} to={draft.runStartTo}
              onChange={(a, b) => setDraft(d => ({ ...d, runStartFrom: a, runStartTo: b }))} />
            <DateRange label="Run End" from={draft.runEndFrom} to={draft.runEndTo}
              onChange={(a, b) => setDraft(d => ({ ...d, runEndFrom: a, runEndTo: b }))} />
          </FilterGroup>

          <FilterGroup title="Tool Results" active={countActive(draft, GROUPS.tools)}>
            <div className="geo-section">
              <label className="geo-check-row">
                <input type="checkbox" checked={draft.readHits} onChange={e => setField('readHits', e.target.checked)} />
                Has ReadHits (bowtie2)
              </label>
              <label className="geo-check-row">
                <input type="checkbox" checked={draft.regionHits} onChange={e => setField('regionHits', e.target.checked)} />
                Has RegionHits (blastN)
              </label>
              <label className="geo-check-row">
                <input type="checkbox" checked={draft.hideIncomplete} onChange={e => setField('hideIncomplete', e.target.checked)} />
                Hide incomplete runs
              </label>
            </div>
            <div className="geo-section">
              <div className="geo-num-row">
                <label>
                  <span className="geo-section-label">Min ReadHits</span>
                  <input type="number" min={0} className="geo-input" placeholder="e.g. 10"
                    value={draft.minReadHits} onChange={e => setField('minReadHits', e.target.value)} />
                </label>
                <label>
                  <span className="geo-section-label">Min RegionHits</span>
                  <input type="number" min={0} className="geo-input" placeholder="e.g. 10"
                    value={draft.minRegionHits} onChange={e => setField('minRegionHits', e.target.value)} />
                </label>
              </div>
            </div>
            <div className="geo-section">
              <div className="geo-section-label">Region (any of)</div>
              <div className="geo-region-grid">
                {REGIONS.map(r => (
                  <button key={r} type="button"
                    className={`geo-region-btn ${draft.regions.includes(r) ? 'geo-region-btn--on' : ''}`}
                    aria-pressed={draft.regions.includes(r)}
                    onClick={() => toggleRegion(r)}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </FilterGroup>

          <div className="geo-divider" />

          <div className="geo-section">
            <div className="geo-section-label">Samples per Country</div>
            <div className="geo-legend-grid">
              {[...COUNT_BUCKETS].reverse().map(({ color, label }) => (
                <div key={label} className="geo-legend-item">
                  <span className="geo-legend-swatch" style={{ background: color }} />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="geo-divider" />

          <div className="geo-section">
            <div className="geo-section-label">Data Summary</div>
            <div className="geo-summary-grid">
              <div className="geo-summary-card">
                <div className="geo-summary-label">Samples</div>
                <div className="geo-summary-val">{sampleTotal.toLocaleString()}</div>
              </div>
              <div className="geo-summary-card">
                <div className="geo-summary-label">Countries</div>
                <div className="geo-summary-val">{countryTotal}</div>
              </div>
            </div>
            {topCountries.length > 0 && (
              <div className="geo-toplist">
                {topCountries.map((c, i) => (
                  <button key={c.name} type="button" className="geo-toplist-row geo-toplist-link"
                    title={`View ${c.labels.join(' / ')} runs in Data Table`}
                    onClick={() => openDatatable(c.labels)}>
                    <span className="geo-toplist-rank">{i + 1}</span>
                    <span className="geo-toplist-name">{c.labels.join(' / ')}</span>
                    <span className="geo-toplist-num">{c.count.toLocaleString()}</span>
                    <span className="geo-toplist-arrow" aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── APPLY BAR ── */}
        <div className="geo-apply-bar">
          <div className="geo-apply-status" aria-live="polite">
            {dirty
              ? <span className="geo-status geo-status--pending">● Changes not applied</span>
              : isDefault(applied)
                ? <span className="geo-status">Default filters</span>
                : appliedCount > 0
                  ? <span className="geo-status geo-status--ok">✓ Filters applied</span>
                  : <span className="geo-status">No filters</span>}
            {dirty && <button type="button" className="geo-link-btn" onClick={discardDraft}>Discard</button>}
          </div>
          <div className="geo-apply-actions">
            <button type="button" className="geo-btn geo-btn-ghost geo-btn-sm" onClick={resetToDefaults}
              disabled={isDefault(draft) && isDefault(applied)}
              title="ReadHits Filter + Region Hits on, everything else cleared">
              ↺ Reset to defaults
            </button>
            <button type="button" className="geo-btn geo-btn-primary geo-btn-sm" onClick={applyFilters}
              disabled={!dirty || isLoading}>
              {isLoading ? 'Applying…' : 'Apply filters'}
            </button>
          </div>
        </div>
      </div>

      {/* ── MAP AREA ── */}
      <div className="geo-map-area">
        <div className="geo-map-header">
          <div className="geo-map-header-left">
            <span className="geo-map-title">Sample Origins (Metadata)</span>
            <span className="geo-map-badge">{countryTotal} Countries</span>
          </div>
          <form className="geo-run-search" role="search" onSubmit={findRun}>
            <input className="geo-input" type="search" placeholder="Find run, e.g. SRR31806855"
              value={runQuery} onChange={e => setRunQuery(e.target.value)} aria-label="Find a run by accession" />
            <button type="submit" className="geo-btn geo-btn-primary geo-btn-sm"
              disabled={!runQuery.trim() || runLookup?.status === 'loading'}>
              {runLookup?.status === 'loading' ? '…' : 'Find'}
            </button>
          </form>
          <div className="geo-pills">
            {isLoading && geo && <div className="geo-spinner" />}
            {QUICK_TOGGLES.map(({ key, label, title }) => (
              <button key={key} type="button" title={title} aria-pressed={applied[key]}
                className={`geo-pill ${applied[key] ? 'geo-pill--on' : ''}`}
                onClick={() => toggleQuick(key)}>
                {applied[key] ? '✓' : '○'} {label}
              </button>
            ))}
          </div>
        </div>

        <div className="geo-map-canvas">
          {isLoading && !geo ? (
            <div className="geo-empty">
              <LoadingSequence message="Loading metadata" size="medium" />
            </div>
          ) : error || countryTotal === 0 ? (
            <div className="geo-empty">
              <div className="geo-empty-icon">◎</div>
              <span>{error || 'No samples match these filters.'}</span>
            </div>
          ) : (
            <CountryMap countries={byIso} selectedIso={selected ? selectedIso : null}
              onSelect={setSelectedIso} tooltipHtml={tooltipHtml} focus={mapFocus}>
              {selected && (
                <DetailPanel key={selectedIso} title={selected.labels.join(' / ')} color={selected.color}
                  onClose={() => setSelectedIso(null)}
                  footer={<>
                    <button type="button" className="geo-btn geo-btn-ghost geo-btn-sm geo-dt-link"
                      onClick={() => filterToCountry(selected.labels)}
                      disabled={selected.labels.every(l => applied.countries.includes(l))}
                      title="Add this country to the Country filter and apply it">
                      Filter map to this country
                    </button>
                    <button type="button" className="geo-btn geo-btn-ghost geo-btn-sm geo-dt-link"
                      onClick={() => onCompareRuns?.(topRuns.runs.slice(0, 5).map(r => r.run))}
                      disabled={!topRuns.runs?.length}
                      title="Load the top 5 runs into the Read Hits tab to compare BLAST-derived origins">
                      Compare top runs in Read Hits tab →
                    </button>
                    <button type="button" className="geo-btn geo-btn-primary geo-btn-sm geo-dt-link"
                      onClick={() => openDatatable(selected.labels)}>
                      View runs in Data Table →
                    </button>
                  </>}>
                  <DetailStats items={[
                    { label: 'Samples', value: (selected.count ?? 0).toLocaleString() },
                    { label: 'Share', value: sampleTotal ? `${((selected.count / sampleTotal) * 100).toFixed(1)}%` : '—' },
                    ...(detail && !detail.error ? [{
                      label: 'With ReadHits',
                      value: `${detail.readHitsRuns.toLocaleString()}${detail.total ? ` (${((detail.readHitsRuns / detail.total) * 100).toFixed(1)}%)` : ''}`,
                    }] : []),
                  ]} />
                  <TopRuns runs={topRuns.runs} loading={topRuns.loading} error={topRuns.error} onOpen={openRun} />
                  {detailLoading && !detail ? (
                    <div className="geo-ms-empty">Loading breakdown…</div>
                  ) : detail?.error ? (
                    <div className="geo-ms-empty">Could not load breakdown.</div>
                  ) : detail ? (
                    <>
                      <TopList title="Top Organisms"         rows={detail.organisms}        total={detail.total} />
                      <TopList title="Top Centers"           rows={detail.centers}          total={detail.total} />
                      <TopList title="Top Platforms"         rows={detail.platforms}        total={detail.total} />
                      <TopList title="Top Isolation Sources" rows={detail.isolationSources} total={detail.total} />
                    </>
                  ) : null}
                </DetailPanel>
              )}
            </CountryMap>
          )}

          {runLookup && runLookup.status !== 'loading' && (
            <div className="geo-run-card" role="status">
              <button type="button" className="geo-chip-x geo-run-card-x" onClick={closeRunCard} title="Close">✕</button>
              {runLookup.status === 'error' ? (
                <div className="geo-run-card-msg">{runLookup.message}</div>
              ) : (() => {
                const r = runLookup.data;
                return (
                  <>
                    <div className="geo-run-card-id">{r.run}</div>
                    <div className="geo-run-card-meta">
                      {[r.country || 'No country in metadata', r.organism, r.platform].filter(Boolean).join(' · ')}
                    </div>
                    <div className="geo-run-card-meta">
                      {[r.center, r.collectionDate && `collected ${r.collectionDate}`].filter(Boolean).join(' · ')}
                    </div>
                    <div className="geo-run-card-stats">
                      <span>ReadHits <b>{r.readHitsCount.toLocaleString()}</b></span>
                      <span>RegionHits <b>{r.regionHitsCount.toLocaleString()}</b></span>
                      {!r.complete && <span className="geo-run-large">running</span>}
                      {r.regionHitsCount > LARGE_RUN_THRESHOLD && <span className="geo-run-large">large</span>}
                    </div>
                    {!r.hasMetadata && <div className="geo-run-card-msg">This run has no sample metadata, so it isn't on the map.</div>}
                    {r.hasMetadata && !r.iso && <div className="geo-run-card-msg">No usable country, so the map can't show where it's from.</div>}
                    <div className="geo-run-card-actions">
                      <button type="button" className="geo-btn geo-btn-primary geo-btn-sm" onClick={() => openRun(r.run, r.regionHitsCount)}>
                        Open in Read Hits →
                      </button>
                      <button type="button" className="geo-btn geo-btn-ghost geo-btn-sm" onClick={() => onCompareRuns?.([r.run])}>
                        Read Hits tab
                      </button>
                      {r.iso && byIso[r.iso] && (
                        <button type="button" className="geo-btn geo-btn-ghost geo-btn-sm" onClick={() => setSelectedIso(r.iso)}>
                          {byIso[r.iso].labels.join(' / ')} details
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <LargeRunModal data={largeRun} onClose={() => setLargeRun(null)}
          onNavigate={(path) => history.push(path)} initialRegions={applied.regions} />

        <div className="geo-disclaimer">
          Country from sample metadata (geo_loc_name_country_calc).
          {noCountry > 0 && ` ${noCountry.toLocaleString()} samples have no usable country and are not shown on the map.`}
        </div>
      </div>
    </>
  );
}
